<template>
  <div class="content">
    <div v-if="loading">
      <LoadingSpinner />
    </div>
    <div v-else>
      <NotificationsComponent context="InvoiceDetailsComponent" />
      <section v-if="pageState === invoicePageState.addPaymentMethod">
        <h1>
          <button type="button" class="inline-btn-link" @click="setPageState(invoicePageState.invoiceDetails)">
            <svg-icon-component icon="chevron-left" />
          </button>
          {{ t("SubscriptionInformationComponent.addPaymentMethod") }}
        </h1>
        <div>
          <i18n-t keypath="SubscriptionInformationComponent.addPaymentHeader" tag="p" class="mt" scope="global">
            <template #all>
              <strong>{{ t("SubscriptionInformationComponent.all") }}</strong>
            </template>
          </i18n-t>
          <SafeProductsInfoComponent :subscriptions="subscriptions" />
        </div>
        <div>
          <AddCreditCardComponent
            :sub="currentSubscription"
            :credit-card-previous-state="invoicePageState.default"
            @subscription-page-state-change="pageState = invoicePageState.invoiceDetails"
            @payment-method-added="addPaymentMethodHandler"
          />
        </div>
      </section>
      <section v-else-if="pageState === invoicePageState.invoiceDetails">
        <h1>
          <button type="button" class="inline-btn-link" @click="cancel()">
            <svg-icon-component icon="chevron-left" />
          </button>
          {{ tn("invoiceDetails") }}
        </h1>
        <ul>
          <li>{{ tn("payWith") }}</li>
          <li v-if="creditCardList.length > 0" class="mb">
            <select
              id="paymentMethodSelect"
              v-model="payOutstandingInvoicesRequest.paymentMethodId"
              class="select-css"
              @change="onPaymentMethodChange()"
            >
              <option v-for="card in creditCardList" :key="card.id" :value="card.id">
                {{ prepareSelectTextForCreditCard(card) }}
              </option>
            </select>
          </li>
          <li v-else class="mb">
            <button id="btnAddNewCard" type="button" class="inline-btn-link" @click="addNewCreditCard()">
              {{ tn("editPayment") }}
            </button>
          </li>
        </ul>
        <ul v-if="paymentMethodChanged && !!selectedCreditCard">
          <li>{{ selectedCreditCard.accountHolder }}</li>
          <li>
            {{ selectedCreditCard.address.streetAddress1 }} {{ selectedCreditCard.address.streetAddress2 }}
            {{ selectedCreditCard.address.streetAddress3 }}
          </li>
          <li class="mb">
            {{ selectedCreditCard.address.city }} {{ selectedCreditCard.address.stateOrProvince }} ,
            {{ selectedCreditCard.address.postalCode }} {{ selectedCreditCard.address.country }}
          </li>
          <li class="mb">
            <button id="btnAddNewCard" type="button" class="inline-btn-link" @click="addNewCreditCard()">
              {{ tn("editPayment") }}
            </button>
          </li>
        </ul>
        <div class="table-to-list-wrapper">
          <table v-if="(outstandingInvoices?.outstandingInvoices?.length ?? 0) > 0">
            <th @click="toggleAllInvoices"><input type="checkbox" :checked="isAllSelected" /></th>
            <th class="sortable" :class="sortDirection.invoiceNumber" @click="sortInvoices('invoiceNumber')">
              {{ tn("invoiceIdCol") }}
            </th>
            <th class="sortable" :class="sortDirection.invoiceDate" @click="sortInvoices('invoiceDate')">
              {{ tn("dateCol") }}
            </th>
            <th class="sortable" :class="sortDirection.packageName" @click="sortInvoices('packageName')">
              {{ tn("subscriptionCol") }}
            </th>
            <th class="sortable" :class="sortDirection.balance" @click="sortInvoices('balance')">
              {{ tn("outstandingBalanceCol") }}
            </th>
            <tr v-for="o in outstandingInvoices?.outstandingInvoices" :key="o.invoiceNumber" @click="toggleInvoice(o)">
              <td :data-th="tn('invoiceIdCol')">
                <input type="checkbox" :checked="isSelected(o)" />
              </td>
              <td :data-th="tn('dateCol')">{{ o.invoiceNumber }}</td>
              <td :data-th="tn('subscriptionCol')">{{ formatDate(o.invoiceDate) }}</td>
              <td :data-th="tn('outstandingBalanceCol')">
                {{ o.packageName }}
                <span v-if="o.isWebroot">{{ tn("keycode") }} {{ o.targetName }}</span>
                <span v-else class="computer-name">
                  {{ tn("computer") }}
                  <span>
                    {{ o.targetName }}
                  </span>
                </span>
              </td>
              <td :data-th="tn('productCol')">{{ formatMoney(o.balance) }}</td>
            </tr>
          </table>
        </div>
        <ul>
          <li v-if="totalUpdate" class="mb">
            {{ tn("invoiceTotal") }}
            <strong>{{ formatMoney(payOutstandingInvoicesRequest.amount ?? 0) }}</strong>
          </li>
          <i18n-t keypath="InvoiceDetailsComponent.continueStatement" tag="li" class="mb" scope="global">
            <template #termsAndConditions>
              <button
                id="btnTermsAndConditions"
                type="button"
                class="inline-btn-link"
                target="_blank"
                @click="goToUrl('https://www.carbonite.com/terms-of-use/product-terms-of-service')"
              >
                {{ tn("termsAndConditions") }}
              </button>
            </template>
            <template #privacyPolicy>
              <button
                id="btnPrivacyPolicy"
                type="button"
                class="inline-btn-link"
                target="_blank"
                @click="goToUrl('https://www.carbonite.com/terms-of-use/privacy-policy')"
              >
                {{ tn("privacyPolicy") }}
              </button>
            </template>
          </i18n-t>
        </ul>
        <div class="mb">
          <button class="btn-link" @click="cancel()">{{ tn("cancel") }}</button>
          <button
            class="btn-primary"
            :disabled="(payOutstandingInvoicesRequest.amount ?? 0) <= 0 || !hasSelectedCC || isRenewing"
            @click="completeRenewNow()"
          >
            <spinner :is-spinning="isRenewing" />
            {{ tn("renewNow") }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, computed } from "vue";
import { useRouter } from "vue-router";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";
import {
  ICreditCard,
  IOutstandingInvoices,
  IPayOutstandingInvoicesRequest,
  IInvoicePaymentData,
  IOutstandingInvoice,
  ISubscription,
} from "@/common/api/unifiedPortal/interfaces";
import { unifiedApi } from "@/common";
import { useNotificationsStore } from "@/stores/notifications";
import { formatDateString } from "@/common/dateFormat";
import { carboniteProducts } from "@/common/productsBySku";
import { pageState as invoicePageState } from "@/globalEnums";
import { pageView, outstandingInvoicePayEvent } from "@/common/googleTagEvents";
import Spinner from "@/components/shared/Spinner.vue";
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue";
import * as creditCardFunctions from "@/components/CreditCards/CreditCards";
import AddCreditCardComponent from "@/components/CreditCards/AddCreditCard.vue";
import SafeProductsInfoComponent from "@/components/Subscriptions/SafeProductsInfo.vue";
import SvgIconComponent from "@/components/shared/SvgIcon/SvgIcon.vue";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import { useBuyFlowStore } from "@/stores/buyFlow";
import NotificationsComponent from "@/components/shared/Notifications.vue";

const componentName = "InvoiceDetailsComponent";

export default defineComponent({
  name: componentName,
  components: {
    LoadingSpinner,
    SafeProductsInfoComponent,
    AddCreditCardComponent,
    Spinner,
    SvgIconComponent,
    NotificationsComponent,
  },
  props: {
    subscriptions: {
      type: Array as PropType<ISubscription[]>,
      required: true,
    },
    creditCards: {
      type: Array as PropType<ICreditCard[]>,
      required: true,
    },
    originFromBackupTab: {
      type: Boolean,
    },
  },
  emits: ["success", "invoice-details-canceled", "subscription-page-state-change", "updated-credit-cards"],

  setup(props, context) {
    logEvent("created", componentName);

    const notificationsStore = useNotificationsStore();
    const buyFlowStore = useBuyFlowStore();
    const isRenewing = ref<boolean>(false);
    const selectedCreditCard = ref<ICreditCard>();
    const outstandingInvoices = ref<IOutstandingInvoices | null>(null);
    const loading = ref<boolean>(true);
    const payOutstandingInvoicesRequest = ref<IPayOutstandingInvoicesRequest>({});
    const totalUpdate = ref<boolean>(false);
    const paymentMethodChanged = ref<boolean>(true);
    const selectedInvoices = ref<IOutstandingInvoice[]>([]);
    const currentSubscription = ref<ISubscription>();
    const pageState = ref<invoicePageState>(invoicePageState.invoiceDetails);
    const creditCardList = ref<ICreditCard[]>([]);
    const hasSelectedCC = ref<boolean>(false);
    const sortColumn = ref<string>("");
    const sortDirection = ref<Record<string, unknown>>({
      invoiceDate: "descend",
      invoiceNumber: "",
      packageName: "",
      balance: "",
    });

    //make a copy of the credit card data so we can update it
    for (const cc of props.creditCards) {
      const ccc = { ...cc };
      ccc.address = { ...cc.address };
      creditCardList.value.push(ccc);
    }

    if (creditCardList.value.length > 0) {
      setDefaultPaymentMethod();
      hasSelectedCC.value = true;
    } else {
      setPageState(invoicePageState.addPaymentMethod);
    }

    initializeInvoiceDetails();
    pageView("invoiceDetails", "payment_form");
    //cSpell:ignore outstandinginvoice, paymentmethod
    //if user has outstanding invoice then url /subscriptions?action=outstandinginvoice&paymentMethod=add will navigate directly to invoice details section
    if (
      useRouter().currentRoute.value.query.action?.toString().toLowerCase() === "outstandinginvoice" &&
      useRouter().currentRoute.value.query.paymentMethod?.toString().toLowerCase() === "add"
    ) {
      setPageState(invoicePageState.addPaymentMethod);
    }

    const isAllSelected = computed(() => {
      return selectedInvoices.value.length === outstandingInvoices.value?.outstandingInvoices.length;
    });

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${componentName}.${v}`, params);
    }

    function isSelected(o: IOutstandingInvoice) {
      return selectedInvoices.value.filter(q => q.invoiceNumber == o.invoiceNumber).length === 1;
    }

    function prepareSelectTextForCreditCard(card: ICreditCard): string {
      return `${card.cardType} *${card.lastDigits.substring(
        card.lastDigits.length - 4,
        card.lastDigits.length
      )} Exp. ${formatDateString(card.expirationDate)}`;
    }

    function setDefaultPaymentMethod() {
      selectedCreditCard.value = creditCardList.value.find(card => card.defaultPaymentMethod === true);
      payOutstandingInvoicesRequest.value.paymentMethodId = selectedCreditCard.value?.id;
      recalculateTotalOutstanding();
    }

    function onPaymentMethodChange() {
      paymentMethodChanged.value = false;
      selectedCreditCard.value = creditCardList.value.find(
        card => card.id === payOutstandingInvoicesRequest.value?.paymentMethodId
      );
      paymentMethodChanged.value = true;
      hasSelectedCC.value = true;
    }

    function toggleAllInvoices() {
      if (outstandingInvoices.value) {
        selectedInvoices.value =
          selectedInvoices.value.length === 0 ? outstandingInvoices.value.outstandingInvoices : [];
      }
      recalculateTotalOutstanding();
    }

    function toggleInvoice(o: IOutstandingInvoice) {
      //get the current count
      const oldCount = selectedInvoices.value.length;
      //filter out the one we want to toggle
      selectedInvoices.value = selectedInvoices.value.filter(i => i.invoiceNumber !== o.invoiceNumber);
      //if it wasn't on the list, then we're adding it
      if (oldCount === selectedInvoices.value.length) selectedInvoices.value.push(o);
      recalculateTotalOutstanding();
    }

    async function completeRenewNow() {
      isRenewing.value = true;
      notificationsStore.clearNotifications();
      payOutstandingInvoicesRequest.value.invoiceData = [];
      for (const invoice of selectedInvoices.value) {
        const invoiceData: IInvoicePaymentData = {
          amount: invoice.balance,
          invoiceId: invoice.invoiceId,
        };

        payOutstandingInvoicesRequest.value?.invoiceData?.push(invoiceData);
      }

      try {
        const response = (await unifiedApi.payOutstandingInvoices(payOutstandingInvoicesRequest.value)).data;

        if (response && response.success) {
          pushGoogleTagData(selectedInvoices.value);
          //if success make that credit card as default, if that is not default yet
          const isDefaultPaymentMethod = creditCardList.value.find(
            x => x.id == payOutstandingInvoicesRequest.value.paymentMethodId
          )?.defaultPaymentMethod;
          if (!isDefaultPaymentMethod && payOutstandingInvoicesRequest.value.paymentMethodId) {
            const cards = await creditCardFunctions.setDefaultCreditCard(
              creditCardList.value,
              payOutstandingInvoicesRequest.value.paymentMethodId
            );
            creditCardList.value = cards ?? [];
          }
          context.emit("success");
        } else {
          if (response && response.errorMessage && response.errorMessage.indexOf("Insufficient Funds") !== -1) {
            notificationsStore.addNotification({ type: "CreditCardInsufficientFunds" });
          } else {
            notificationsStore.addNotification({ type: "RenewFailureMessage", dismissible: false });
            logEvent("RenewFailureMessage");
          }
        }
      } catch (err) {
        handleApiError(err as AxiosError);
      }

      isRenewing.value = false;
    }

    function pushGoogleTagData(invoiceData: IOutstandingInvoice[]) {
      invoiceData.forEach(item => {
        outstandingInvoicePayEvent(item);
      });
    }

    function recalculateTotalOutstanding() {
      totalUpdate.value = false;
      let totalAmount = 0;
      for (const invoice of selectedInvoices.value) {
        totalAmount += invoice.balance;
      }

      if (payOutstandingInvoicesRequest.value) payOutstandingInvoicesRequest.value.amount = totalAmount;
      totalUpdate.value = true;
    }

    function cancel() {
      if (props.originFromBackupTab) {
        context.emit("invoice-details-canceled");
        return;
      }

      context.emit("subscription-page-state-change", invoicePageState.default);
    }

    function addNewCreditCard() {
      notificationsStore.clearNotifications();
      pageState.value = invoicePageState.addPaymentMethod;
      logEvent("Page state change", pageState.value);
    }

    function formatDate(date: string) {
      return formatDateString(date);
    }
    function formatMoney(amount: number) {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
    }

    async function initializeInvoiceDetails() {
      try {
        outstandingInvoices.value = processInvoiceData((await unifiedApi.getOutstandingInvoices()).data);
        toggleAllInvoices();
        sortInvoices("invoiceDate");
        loading.value = false;
      } catch (err) {
        handleApiError(err as AxiosError);
      }
    }

    function processInvoiceData(outstandingInvoices: IOutstandingInvoices) {
      for (const invoice of outstandingInvoices.outstandingInvoices) {
        const invoiceId = invoice.invoiceId;
        let targetName = "";
        let packageName = "";
        //get all matching invoices from subscription Data and update package name and computer name
        for (const sub of props.subscriptions) {
          if (sub.hasOutstandingInvoices && sub.outstandingInvoice?.invoiceId === invoiceId) {
            const isSafeSKU = carboniteProducts.find(product => product.sku === sub.sku);
            if (isSafeSKU) {
              //for carbonite show computer description
              targetName =
                targetName === "" ? `${sub.computer?.description}` : `${targetName} , ${sub.computer?.description}`;
              packageName = packageName === "" ? `${sub?.packageName}` : `${packageName} , ${sub?.packageName}`;
            } else {
              //for webroot show keycode
              targetName = targetName === "" ? `${sub.keyCode}` : `${targetName} , ${sub.keyCode}`;
              packageName =
                packageName === "" ? `${sub.productDisplayName}` : `${packageName} , ${sub.productDisplayName}`;
              invoice.isWebroot = true;
            }
          }
        }
        invoice.packageName = packageName;
        invoice.targetName = targetName;
      }
      return outstandingInvoices;
    }

    function onSelectInvoice(items) {
      selectedInvoices.value = items;
      recalculateTotalOutstanding();
    }

    async function addPaymentMethodHandler() {
      buyFlowStore.showProcessingOrderMessage = false;
      notificationsStore.clearNotifications();
      notificationsStore.addNotification({ type: "AddCreditCardSuccess" });
      //move to invoice details page and load new credit cards from server
      setCreditCardsAfterSuccess();
      pageState.value = invoicePageState.invoiceDetails;
      logEvent("Page state change", pageState.value);
    }

    async function setPageState(state: string) {
      notificationsStore.clearNotifications();
      pageState.value = invoicePageState[state];
      logEvent("Page state change", pageState.value);
    }

    async function setCreditCardsAfterSuccess() {
      try {
        creditCardList.value = (await unifiedApi.getUserCreditCards()).data;
        //After getting the list latest Credit Card from API,update the parent pages with latest credit card and set default credit card also.
        setDefaultPaymentMethod();
        hasSelectedCC.value = true;
        context.emit("updated-credit-cards", creditCardList.value);
      } catch (err) {
        handleApiError(err as AxiosError);
      }
    }

    function sortInvoices(field: string) {
      //turn off sorting indicator on old column
      if (sortColumn.value !== field) sortDirection.value[sortColumn.value] = "";

      //pick a direction for the sort
      if (sortDirection.value[field] == "ascend") sortDirection.value[field] = "descend";
      else if (sortDirection.value[field] == "descend") sortDirection.value[field] = "ascend";
      else sortDirection.value[field] = "ascend";

      const order = sortDirection.value[field] === "ascend" ? -1 : 1;

      if (outstandingInvoices.value)
        outstandingInvoices.value.outstandingInvoices = outstandingInvoices.value.outstandingInvoices.sort(
          (a: IOutstandingInvoice, b: IOutstandingInvoice) => {
            if (a[field] > b[field]) return order;
            if (a[field] < b[field]) return -order;
            return 0;
          }
        );
      sortColumn.value = field;
    }

    function goToUrl(url: string): void {
      window.open(url, "_blank");
    }

    //Expose to template
    return {
      t,
      tn,
      pageState,
      setPageState,
      currentSubscription,
      addPaymentMethodHandler,
      cancel,
      payOutstandingInvoicesRequest,
      prepareSelectTextForCreditCard,
      paymentMethodChanged,
      selectedCreditCard,
      formatDate,
      loading,
      invoicePageState,
      addNewCreditCard,
      completeRenewNow,
      onPaymentMethodChange,
      selectedInvoices,
      onSelectInvoice,
      outstandingInvoices,
      totalUpdate,
      isRenewing,
      hasSelectedCC,
      sortInvoices,
      toggleInvoice,
      toggleAllInvoices,
      isSelected,
      isAllSelected,
      creditCardList,
      sortColumn,
      sortDirection,
      formatMoney,
      goToUrl,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";

@media (min-width: 600px) {
  .select-css {
    width: 50%;
  }
}

.computer-name {
  max-width: 200px;
  display: flex;
}

.computer-name span {
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
}
</style>
